import React, { useState, forwardRef } from 'react';
import { FormattedMessage, FormattedPlural } from 'react-intl';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { DateTime } from 'luxon';
// import { useQuery } from '@apollo/client';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

import {
  Root,
  LoadingContainer,
  ActiveFilters,
  TableScrollContainer,
  Table,
} from './EducationSessionRequest.style';

import { Typography } from '../../ui/Typography/Typography';
import { PageSubheader } from '../PageSubheader/PageSubheader';
import { ButtonDropdown } from '../../ui/ButtonDropdown/ButtonDropdown';
import { Pagination } from '../Pagination/Pagination';
import { Loading } from '../Loading/Loading';
import { NoResults } from '../Dashboard/NoResults';
import { Tag } from '../Tag/Tag';

const columnHelper = createColumnHelper();
const columns = [
  columnHelper.accessor('dateRequested', {
    header: 'Date Requested',
    cell: (info) => DateTime.fromISO(info.getValue()).toFormat('LL/dd/yyyy'),
    enableSorting: true,
  }),
  columnHelper.accessor('status', {
    header: 'Status',
    cell: (info) => info.getValue().toUpperCase(),
    enableSorting: true,
  }),
  columnHelper.accessor('startDate', {
    header: 'Start Date',
    cell: (info) => {
      const val = info.getValue();
      return val ? (
        DateTime.fromISO(val).toFormat('LL/dd/yyyy')
      ) : (
        <Tag as="span">Not scheduled</Tag>
      );
    },
    enableSorting: true,
  }),
];

export const RequestHistory = () => {
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [sorting, setSorting] = useState([]);
  const [page, setPage] = useState(1);

  const filters = [];
  if (startDate) {
    filters.push({
      key: 'dateRequested',
      operator: 'dateGTE',
      value: DateTime.fromJSDate(startDate).startOf('day').toISO(),
    });

    if (!endDate) {
      filters.push({
        key: 'dateRequested',
        operator: 'dateLTE',
        value: DateTime.fromJSDate(startDate).endOf('day').toISO(),
      });
    }
  }

  if (endDate) {
    filters.push({
      key: 'dateRequested',
      operator: 'dateLTE',
      value: DateTime.fromJSDate(endDate).endOf('day').toISO(),
    });
  }

  // const {
  //   loading: historyLoading,
  //   error: historyError,
  //   data: historyData,
  // } = useQuery(GET_REQUEST_HISTORY, {
  //   variables: {
  //     page,
  //     sort: sorting.map((itm) => ({
  //       key: itm.id,
  //       direction: itm.desc ? '-1' : '1',
  //     })),
  //     filters,
  //   },
  // });

  const historyLoading = false;
  const history = {
    docs: [],
  };

  const table = useReactTable({
    data: history?.docs ?? [],
    columns,
    state: {
      sorting,
    },
    onSortingChange: (sortingUpdater) => {
      const newSorting = sortingUpdater(sorting);
      setSorting(newSorting);
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <Root>
      <Typography type="h2">
        <FormattedMessage defaultMessage="Request History" />
      </Typography>

      <PageSubheader>
        <Typography type="text-small">
          <span>
            {history?.docs?.length ?? 0}{' '}
            <FormattedPlural
              value={history?.docs?.length ?? 0}
              one="result"
              other="results"
            />
          </span>

          <ActiveFilters>
            {startDate && (
              <>
                {' '}
                <b>Filtered:</b>{' '}
                {DateTime.fromJSDate(startDate).toFormat('yyyy LLL dd')}
              </>
            )}

            {endDate && (
              <>
                {' to '}
                {DateTime.fromJSDate(endDate).toFormat('yyyy LLL dd')}
              </>
            )}
          </ActiveFilters>
        </Typography>

        <DatePicker
          selected={startDate}
          onChange={(dates) => {
            const [start, end] = dates;
            setStartDate(start);
            setEndDate(end);
          }}
          startDate={startDate}
          endDate={endDate}
          customInput={<CustomReactDateInput />}
          enableTabLoop={false}
          selectsRange
        />
      </PageSubheader>

      {historyLoading && (
        <LoadingContainer>
          <Loading global={false} />
        </LoadingContainer>
      )}

      {!historyLoading && history?.docs?.length === 0 && <NoResults />}

      {!historyLoading && history?.docs?.length > 0 && (
        <>
          <TableScrollContainer>
            <Table width="100%">
              <thead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => {
                      return (
                        <th key={header.id} colSpan={header.colSpan}>
                          {header.isPlaceholder ? null : (
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                columnGap: 8,
                                cursor: header.column.getCanSort()
                                  ? 'pointer'
                                  : 'default',
                              }}
                              {...{
                                onClick:
                                  header.column.getToggleSortingHandler(),
                              }}
                            >
                              <Typography type="text-small" color="#8f95a5">
                                {flexRender(
                                  header.column.columnDef.header,
                                  header.getContext()
                                )}
                              </Typography>

                              {header.column.getCanSort() && (
                                <>
                                  {{
                                    asc: (
                                      <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 16 16"
                                        width={16}
                                        height={16}
                                      >
                                        <path
                                          fill="#8f95a5"
                                          d="M10.647 6.339 8.044 3.736h-.092L5.354 6.34l-.706-.705 2.895-2.899h.917l2.896 2.9-.709.703Z"
                                        />
                                      </svg>
                                    ),
                                    desc: (
                                      <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 16 16"
                                        width={16}
                                        height={16}
                                      >
                                        <path
                                          fill="#8f95a5"
                                          d="m5.356 9.737 2.603 2.603h.092l2.598-2.604.706.705L8.46 13.34h-.917l-2.896-2.9.709-.703Z"
                                        />
                                      </svg>
                                    ),
                                  }[header.column.getIsSorted()] ?? (
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      viewBox="0 0 16 16"
                                      width={16}
                                      height={16}
                                    >
                                      <path
                                        fill="#8f95a5"
                                        d="m5.356 9.737 2.603 2.603h.092l2.598-2.604.706.705L8.46 13.34h-.917l-2.896-2.9Zm5.291-3.398L8.044 3.736h-.092L5.354 6.34l-.706-.705 2.895-2.899h.917l2.896 2.9Z"
                                      />
                                    </svg>
                                  )}
                                </>
                              )}
                            </div>
                          )}
                        </th>
                      );
                    })}
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.map((row) => {
                  return (
                    <tr key={row.id}>
                      {row.getVisibleCells().map((cell) => {
                        return (
                          <td key={cell.id}>
                            <Typography type="text-secondary">
                              {flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext()
                              )}
                            </Typography>
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </TableScrollContainer>

          <Pagination
            append={
              <Typography type="text-small" color="#8f95a5">
                {history?.docs?.length ?? 0}{' '}
                <FormattedPlural
                  value={history?.docs?.length ?? 0}
                  one="result"
                  other="results"
                />
              </Typography>
            }
            totalCount={history?.totals ?? 0}
            currentPage={history?.page ?? 1}
            pageSize={history?.limit ?? 15}
            onPaginate={setPage}
            style={{ marginTop: 48 }}
          />
        </>
      )}
    </Root>
  );
};

const CustomReactDateInput = forwardRef(({ onClick }, ref) => (
  <ButtonDropdown type="button" onClick={onClick} ref={ref}>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 16 16"
      width={16}
      height={16}
    >
      <path
        fill="currentColor"
        d="M3.5 7h1v1h-1Zm2 0h1v1h-1Zm2 0h1v1h-1Zm2 0h1v1h-1Zm2 0h1v1h-1Zm-8 2h1v1h-1Zm2 0h1v1h-1Zm2 0h1v1h-1Zm2 0h1v1h-1Zm2 0h1v1h-1Zm-8 2h1v1h-1Zm2 0h1v1h-1Zm2 0h1v1h-1Zm2 0h1v1h-1Zm2 0h1v1h-1Zm2.499-8v11h-12V3h2V2h1v1h6V2h1v1Zm-11 1v1.5h10V4h-1v.5h-1V4h-6v.5h-1V4Zm0 9h9.5l.5-.5v-6h-10Z"
      />
    </svg>
    <Typography type="text-small">
      <FormattedMessage defaultMessage="Select a date" />
    </Typography>
  </ButtonDropdown>
));
